import React, { useState, useEffect, useMemo } from 'react';
import EditImageModal from '../components/EditImageModal';
import ImageModal from '../components/ImageModal';
import { useCakes } from '../context/CakesContext';
import './GalleryPage.css';

const GalleryPage = ({ isAdminMode = false }) => {
  const { cakes, loading, error, updateCake, getAllTags } = useCakes();
  const [filteredCakes, setFilteredCakes] = useState([]);
  const [displayedCakes, setDisplayedCakes] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedCake, setSelectedCake] = useState(null);
  const [editingCake, setEditingCake] = useState(null);
  const [authToken] = useState(localStorage.getItem('adminToken'));
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const ITEMS_PER_PAGE = 20;

  // Get all tags from shared context
  const allTags = useMemo(() => getAllTags(), [getAllTags]);

  // Initialize filtered cakes when cakes load
  useEffect(() => {
    setFilteredCakes(cakes);
  }, [cakes]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeFilter === 'All') {
      setFilteredCakes(cakes);
    } else {
      const filtered = cakes.filter(cake => cake.tags && cake.tags.includes(activeFilter));
      setFilteredCakes(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [activeFilter, cakes]);

  // Pagination effect
  useEffect(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setDisplayedCakes(filteredCakes.slice(start, end));
  }, [currentPage, filteredCakes, ITEMS_PER_PAGE]);

  const totalPages = Math.ceil(filteredCakes.length / ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (id, updatedData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cakes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) throw new Error('Failed to update metadata');

      // Update the shared context cache
      updateCake(id, updatedData);
      setEditingCake(null);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  if (loading) return <div className="gallery-page"><div className="loading-container"><p>Loading our delicious creations...</p></div></div>;
    if (error) return <div className="gallery-page"><div className="error-container"><p>Error loading gallery: {error}</p></div></div>;

  const handleNext = () => {
    const currentIndex = filteredCakes.findIndex(c => c.id === selectedCake.id);
    const nextIndex = (currentIndex + 1) % filteredCakes.length;
    setSelectedCake(filteredCakes[nextIndex]);
  };

  const handlePrev = () => {
    const currentIndex = filteredCakes.findIndex(c => c.id === selectedCake.id);
    const prevIndex = (currentIndex - 1 + filteredCakes.length) % filteredCakes.length;
    setSelectedCake(filteredCakes[prevIndex]);
  };

  return (
    <div className="gallery-page">
      <div className="gallery-header">
        <h1 className="page-title">Our Gallery</h1>
        <p className="gallery-subtitle">Explore our collection of handcrafted cakes, each made with love and attention to detail.</p>
      </div>

      {isMobile && (
        <button
          className="filter-toggle-btn"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          aria-expanded={filtersExpanded}
          aria-label="Toggle filters"
        >
          <i className={`fas fa-${filtersExpanded ? 'chevron-up' : 'chevron-down'}`}></i>
          {filtersExpanded ? 'Hide Filters' : 'Show Filters'} ({allTags.length})
        </button>
      )}

      {(!isMobile || filtersExpanded) && (
      <div className="filter-bar">
        {allTags.map(tag => (
          <button
            key={tag}
            className={`filter-btn ${activeFilter === tag ? 'active' : ''}`}
            onClick={() => setActiveFilter(tag)}
          >
            {tag === 'All' ? 'All Cakes' : `#${tag}`}
          </button>
        ))}
      </div>
      )}

      <div className="gallery-stats">
        <p>Showing {displayedCakes.length} of {filteredCakes.length} cakes (Page {currentPage} of {totalPages})</p>
      </div>
      
      <div className="gallery-grid">
        {displayedCakes.map(cake => (
          <div key={cake.id} className="gallery-item">
            <div className="gallery-image-container" onClick={() => setSelectedCake(cake)}>
              <img 
                src={cake.src} 
                alt={cake.alt} 
                className="gallery-image"
                loading="lazy"
              />
              <div className="gallery-overlay">
                {cake.tags && cake.tags.length > 0 && (
                  <div className="gallery-tags">
                    {cake.tags.map(tag => <span key={tag} className="gallery-tag">#{tag}</span>)}
                  </div>
                )}
              </div>
            </div>
            <h3 className="gallery-cake-name">{cake.name}</h3>
            {(authToken || isAdminMode) && (
              <button className="edit-btn" onClick={(e) => { e.stopPropagation(); setEditingCake(cake); }}>
                Edit
              </button>
            )}
          </div>
        ))}
      </div>
      
      {filteredCakes.length === 0 && (
        <div className="no-results">
          <p>No cakes found for "{activeFilter}". Try a different filter!</p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn" 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          <div className="pagination-info">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
          </div>
          <button 
            className="pagination-btn" 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
      
      {selectedCake && (
        <ImageModal 
          cake={selectedCake} 
          onClose={() => setSelectedCake(null)} 
          onNext={handleNext} 
          onPrev={handlePrev} 
        />
      )}

      {editingCake && (
        <EditImageModal 
          image={editingCake} 
          onClose={() => setEditingCake(null)} 
          onSave={handleSave} 
        />
      )}
    </div>
  );
};

export default GalleryPage;
